import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { BehaviorSubject, Observable, Subject, combineLatest, forkJoin } from 'rxjs';
import { catchError, map, mergeMap, startWith, tap } from 'rxjs/operators';

import { MatDialog } from '@angular/material/dialog';

import { JSR310ConversionService } from 'src/main/angular/app/shared/jsr310-localdates/services/jsr310-conversion.service';
import { MandatoryMap } from '../../../../shared/helpers/mandatory-map.class';
import { MessageDialogComponent } from '../../../../shared/dialogs/components/message-dialog.component';
import { SubjectRelay } from '../../../../shared/commands/subject-relay.class';

import { AntenneService } from '../../../structure/antenne/services/antenne.service';
import { AntenneTLR } from '../../../structure/antenne/entities/antenne-tlr.entity';

import { OuvrageService } from '../../../structure/ouvrage/services/ouvrage.service';
import { OuvrageTLR } from '../../../structure/ouvrage/entities/ouvrage-tlr.entity';

import { ParcService } from '../../../structure/parc/services/parc.service';
import { ParcTLR } from '../../../structure/parc/entities/parc-tlr.entity';

import { SousParcService } from '../../../structure/sous-parc/services/sous-parc.service';
import { SousParcTLR } from '../../../structure/sous-parc/entities/sous-parc-tlr.entity';

import { StaticTypeService } from '../../../../static-types/services/static-types.service';
import { ParcMaintenance } from '../../../../static-types/entities/parc-maintenance.entity';
import { ParcTechnologie } from '../../../../static-types/entities/parc-technologie.entity';

import { TransfertResponsabiliteOrder } from '../entities/transfert-responsabilite-order.entity';
import { TransfertResponsabiliteService } from '../services/transfert-responsabilite.service';

import { UserService } from '../../user/services/user.service';
import { UserTLR } from '../../user/entities/user-tlr.entity';
import { TypeResponsabilite } from '../../../perimetres/entities/type-responsabilite.entity';

import { ParcHyperviseur } from '../../../structure/parc/entities/parc-hyperviseur.entity';

type AntenneData = {
	entity: AntenneTLR,
	parcs: ParcData[] | null,
}

type ParcData = {
	entity: ParcTLR,
	sousParcs: SousParcData[],
	ouvrages: OuvrageTLR[],
}

type SousParcData = {
	entity: SousParcTLR,
	ouvrages: OuvrageTLR[],
}

const typeResponsabiliteCac: TypeResponsabilite = 'CAC';

@Component({
	selector: 'transfert-configurator',
	templateUrl: 'transfert-configurator.html',
	styleUrls: ['transfert-configurator.css']
})
export class TransfertConfiguratorComponent {
	loaded = false;
	candidatKeyGetter = (user: UserTLR | null) => user ? user.id : null;
	candidatLabelGetter = (user: UserTLR) => ['(' + user.role + ')', user.firstName, user.lastName].join(' ');
	technologieLabelGetter = (tech: ParcTechnologie) => tech.label;
	technologiePreselector = (_tech: ParcTechnologie) => true;
	technologieResetCommand = new Subject<void>();
	maintenanceLabelGetter = (maintenance: ParcMaintenance) => maintenance.label;
	maintenancePreselector = (_maintenance: ParcMaintenance) => true;
	
	maintenanceResetCommand = new Subject<void>();
	hyperviseurLabelGetter = (hyperviseur: ParcHyperviseur) => hyperviseur.label;       
	hyperviseurPreselector = (_hyperviseur: ParcHyperviseur) => true;                 
	hyperviseurResetCommand = new Subject<void>();                                     

	user!: UserTLR;
	isCce!: boolean;
	antennes!: AntenneTLR[];
	antennesById!: MandatoryMap<number, AntenneTLR>;
	candidats!: UserTLR[];
	technologies!: ParcTechnologie[];
	maintenances!: ParcMaintenance[];
	perimetre: AntenneData[] = [];
	hyperviseurs: ParcHyperviseur[] = ParcHyperviseur.ALL;

	toutesAntennes!: boolean;
	antennesSelected!: boolean[];
	destinataire!: UserTLR | null;
	transfertComplet!: boolean;
	selectedTechnologies!: ParcTechnologie[];
	toutesTechnologies: boolean = true;
	selectedMaintenance!: ParcMaintenance[];
	toutesMaintenances: boolean = true;
	
	perimetreAntenneComplet!: boolean[];
	perimetreParcComplet!: boolean[];
	perimetreSousParcComplet!: boolean[];
	perimetreOuvrageSelected!: boolean[];

	validationButtonDisabled$!: Observable<boolean>;
	waitingForServer$ = new BehaviorSubject<boolean>(false);
	shouldEvaluateSendAbility$ = new BehaviorSubject<boolean>(false);
	transfertComplet$ = new BehaviorSubject<boolean>(true);
	typeResponsabilite$ = new BehaviorSubject<TypeResponsabilite>(typeResponsabiliteCac);
	typeResponsabilite: TypeResponsabilite = typeResponsabiliteCac; 
	
	selectedHyperviseurs!: ParcHyperviseur[];
	toutesHyperviseurs: boolean = true;
	
	constructor(
		private dialog: MatDialog,
		
		@Inject(AntenneService) antenneService: AntenneService,
		@Inject(OuvrageService) ouvrageService: OuvrageService,
		@Inject(ParcService) parcService: ParcService,
		@Inject(SousParcService) sousParcService: SousParcService,
		@Inject(TransfertResponsabiliteService) private transfertResponsabiliteService: TransfertResponsabiliteService,
		@Inject(UserService) private userService: UserService,

		@Inject(JSR310ConversionService) private conversionService: JSR310ConversionService,
		@Inject(StaticTypeService) staticTypeService: StaticTypeService,
	) {
		////////////////////////////////////////////
		// Pre-chargement des donnees "statiques" //
		////////////////////////////////////////////

		let user$ = userService.getCurrentUserTLR().pipe(
			tap(
				(user) => this.user = user
			)
		);

		let isCce$ = user$.pipe(
			map(
				(user) => user.role === 'CCE'
			),
			tap(
				(isCce) => this.isCce = isCce
			)
		);

		let antennes$ = forkJoin({
			user: user$,
			isCce: isCce$,
		}).pipe(
			mergeMap(
				({ user, isCce }) => isCce ? antenneService.getAntenneTLRList() : forkJoin([antenneService.getAntenneTLRById(user.antenneId)])
			),
			tap(
				(antennes) => this.antennes = antennes
			)
		);

		let antennesById$ = antenneService.getAntenneTLRMapById().pipe(
			map(
				(map) => new MandatoryMap<number, AntenneTLR>(map, "Antenne")
			),
			tap(
				(map) => this.antennesById = map
			)
		);

		let technologies$ = staticTypeService.getParcTechnologieList().pipe(
			tap(
				(technologies) => this.technologies = technologies
			)
		);

		let maintenances$ = staticTypeService.getParcMaintenanceList().pipe(
			tap(
				(maintenances) => this.maintenances = maintenances
			)
		);

		// Combinaison de toutes les informations qui doivent etre chargees avant meme que l'ecran puisse s'afficher
		let preloadedData$ = forkJoin({
			user: user$,
			isCce: isCce$,
			antennes: antennes$,
			antennesById: antennesById$,
			technologies: technologies$,
			maintenances: maintenances$,
		});

		///////////////////////////////////
		// Affichage initial des donnees //
		///////////////////////////////////

		preloadedData$.subscribe(
			(_) => {
				this.presetValues();

				// Indiquer a la vue que le chargement des donnees est termine
				this.loaded = true;
			}
		)

		///////////////////////////////////////////////////////////////////////////////////
		// Rafraichissement des donnees dependant du contexte actuel                     //
		// (essentiellement: quels sont les ouvrages pour lequel l'utilisateur est CAC?) //
		///////////////////////////////////////////////////////////////////////////////////
		
		let parcsById$ = parcService.getParcTLRMapById().pipe(
			map(
				(map) => new MandatoryMap<number, ParcTLR>(map, "Parc")
			)
		);
		
		let sousParcsById$ = sousParcService.getSousParcTLRMapById().pipe(
			map(
				(map) => new MandatoryMap<number, SousParcTLR>(map, "SousParc")
			)
		);
		
		let userAssignements$ = this.refreshCommands$.pipe(
			tap(
				(typeResponsabilite) => {
					this.typeResponsabilite$.next(typeResponsabilite);
					this.typeResponsabilite = typeResponsabilite;
					this.transfertComplet = true;
					this.updateCandidats();
				}
			),
			mergeMap(
				(typeResponsabilite) => forkJoin({
					ouvrages: ouvrageService.getOuvragesTLRByResponsabilite(this.user.id, typeResponsabilite),
					antennes: antenneService.getAntennesTLRByResponsabilite(this.user.id, typeResponsabilite),
				})
			)
		)
		
		// Re-cree la hierarchie dont les ouvrages assignes font partie
		let perimetre$ = combineLatest([parcsById$, sousParcsById$, userAssignements$]).pipe(
			map(
				([parcsById, sousParcsById, userAssignements]) => {
					// Separer les ouvrages qui sont relies a un sous parc,
					// et ceux qui sont directement rattaches a un parc
					let ouvragesBySousParcId = new Map<number, OuvrageTLR[]>();
					let ouvragesByParcId = new Map<number, OuvrageTLR[]>();
					for (let ouvrage of userAssignements.ouvrages) {
						let relevantMap = ouvrage.sousParcId ? ouvragesBySousParcId : ouvragesByParcId;
						let relevantKey = ouvrage.sousParcId ? ouvrage.sousParcId: ouvrage.parcId;
						let list = relevantMap.get(relevantKey);
						if (list) {
							list.push(ouvrage);
						} else {
							relevantMap.set(relevantKey, [ouvrage]);
						}
					}
					
					// Construire les objets SousParcData
					let sousParcDatas = [];
					for (let [sousParcId, ouvrages] of ouvragesBySousParcId.entries()) {
						let sousParc = sousParcsById.get(sousParcId);
						let sousParcData = {
							entity: sousParc,
							ouvrages: ouvrages,
						} as SousParcData;
						sousParcDatas.push(sousParcData);
					}
					
					// Les trier par nom
					sousParcDatas = sousParcDatas.sort(
						(a, b) => a.entity.code.localeCompare(b.entity.code)
					);
					
					// Les ordonner par parc
					let sousParcByParc = new Map<number, SousParcData[]>();
					for (let sousParcData of sousParcDatas) {
						let parcId = sousParcData.entity.parcId;
						let list = sousParcByParc.get(parcId);
						if (list) {
							list.push(sousParcData);
						} else {
							sousParcByParc.set(parcId, [sousParcData]);
						}
					}
					
					// Creer les objets ParcData
					let parcDatas = [];
					for (let [parcId, sousParcDatasForParc] of sousParcByParc.entries()) {
						let parc = parcsById.get(parcId);
						let ouvragesForParc = ouvragesByParcId.get(parcId);
						let parcData = {
							entity: parc,
							sousParcs: sousParcDatasForParc,
							ouvrages: ouvragesForParc ? ouvragesForParc : [],
						} as ParcData;
						// Retirer l'entree dans ouvragesByParcId pour ne pas les traiter en double apres
						ouvragesByParcId.delete(parcId);
						parcDatas.push(parcData);
					}
					for (let [parcId, ouvragesForParc] of ouvragesByParcId.entries()) {
						let parc = parcsById.get(parcId);
						let parcData = {
							entity: parc,
							sousParcs: [],
							ouvrages: ouvragesForParc,
						} as ParcData;
						parcDatas.push(parcData);
					}
					
					// Trier les parc par leur code
					parcDatas = parcDatas.sort(
						(a, b) => a.entity.code.localeCompare(b.entity.code)
					);
					
					// Enfin organiser ces ParcData par antenne
					let parcsDataByAntenne = new Map<number, ParcData[]>();
					for (let parcData of parcDatas) {
						let antenneId = parcData.entity.antenneId;
						let list = parcsDataByAntenne.get(antenneId);
						if (list) {
							list.push(parcData);
						} else {
							parcsDataByAntenne.set(antenneId, [parcData]);
						}
					}
					
					// Et creer les AntenneData
					let antennesDatas = [] as AntenneData[];
					for (let antenne of this.antennes) {
						let parcsOfAntenne = parcsDataByAntenne.get(antenne.id);
						if (parcsOfAntenne) {
							parcsOfAntenne = parcsOfAntenne.filter( pof => pof.entity.actif );
						}
						let data = {
							entity: antenne,
							parcs: parcsOfAntenne? parcsOfAntenne : null,							
						};
						antennesDatas.push(data);
					}
					
					return {
						antennesDatas: antennesDatas,
						antennesCompletes: userAssignements.antennes,
					};
				}
			)
		);
		
		perimetre$.subscribe(
			(perimetre) => {
				// Mettre le perimetre de l'utilisateur a la disposition de la vue
				this.perimetre = perimetre.antennesDatas;
				
				// Mettre a jour la selection par defaut
				this.updatePerimetreSelection();
			}
		);
		
		this.shouldEvaluateSendAbility$.pipe(
			startWith(true)
		)
		
		let mandatoryInputs$ = combineLatest([this.typeResponsabilite$, this.shouldEvaluateSendAbility$, this.transfertComplet$]);
		this.validationButtonDisabled$ = combineLatest([this.waitingForServer$, mandatoryInputs$]).pipe(
			map(
				([waitingForServer, [typeResponsabilite, shouldEvaluateSendAbility, transfertComplet]]) => {
					let shouldDisableValidationButton = false;
					if (!transfertComplet) {
						this.transfertComplet = false;
						let selectedOuvrageIds: number[] = [];			
						for (let antenne of this.perimetre) {
							if (antenne.parcs != null && this.antennesSelected[antenne.entity.id]) {
								for (let parc of antenne.parcs) {
									parc.ouvrages //
										.filter($ => this.perimetreOuvrageSelected[$.id]) //
										.forEach($ => selectedOuvrageIds.push($.id)) //
									;
									for (let sousParc of parc.sousParcs) {
										sousParc.ouvrages //
											.filter($ => this.perimetreOuvrageSelected[$.id]) //
											.forEach($ => selectedOuvrageIds.push($.id)) //
										;
									}
								}
							}
						}
						shouldDisableValidationButton = selectedOuvrageIds.length === 0;
					}
					return waitingForServer || shouldDisableValidationButton || !this.destinataire;
				}
			)
		);
	}
	
	@Output() transfertDemande = new EventEmitter<void>();
	
	get afficherFiltreHyperviseur(): boolean {   
		return !this.toutesTechnologies
			&& !!this.selectedTechnologies
			&& this.selectedTechnologies.length === 1
			&& this.selectedTechnologies[0].label === 'Solaire';
	}
	
	private presetValues() {
		// Cocher toutes les cases d'antenne par defaut
		this.toutesAntennes = true;
		this.antennesSelected = [];
		this.antennes.forEach(
			(antenne) => this.antennesSelected[antenne.id] = true
		)

		// Charger les candidats par defaut
		this.updateCandidats();
		this.destinataire = null;

		this.transfertComplet = true;
		this.technologieResetCommand.next();

		this.perimetreAntenneComplet = [];
		this.perimetreParcComplet = [];
		this.perimetreSousParcComplet = [];
		this.perimetreOuvrageSelected = [];
		
		this.transfertComplet = true;
		this.technologieResetCommand.next();
		this.toutesHyperviseurs = true;
		this.hyperviseurResetCommand.next();
	}

	private refreshCommands$ = new SubjectRelay<TypeResponsabilite>();
	@Input() set refreshCommands(obs: Observable<TypeResponsabilite> | null) {
		this.refreshCommands$.registerSource(obs);
	}
	
	private updatePerimetreSelection() {
		// Definir les deux controles necessaire a la selection
		let isSelectedTechno = (_: string | null) => true;
		if (!this.toutesTechnologies) {
			let techValues = this.selectedTechnologies.map($ => $.label);
			isSelectedTechno = (tech: string | null) => techValues.findIndex($ => $ === tech) >= 0; 
		}
		let isSelectedMaint = (_: string | null) => true;
		if (!this.toutesMaintenances) {
			let maintValues = this.selectedMaintenance.map($ => $.label);
			isSelectedMaint = (maint: string | null) => maintValues.findIndex($ => $ === maint) >= 0; 
		}
		let isSelectedHyperviseur = (_: string | null) => true;          
		if (this.afficherFiltreHyperviseur && !this.toutesHyperviseurs) {  
			let hyperviseurValues = this.selectedHyperviseurs.map($ => $.label);  
			isSelectedHyperviseur = (hyperviseur: string | null) => hyperviseurValues.findIndex($ => $ === hyperviseur) >= 0;  
		}   
		// Placer le bon etat de selection sur element
		this.perimetreAntenneComplet = [];
		this.perimetreParcComplet = [];
		this.perimetreSousParcComplet = [];
		for (let antenne of this.perimetre) {
			// Ignorer les antennes sans parcs inclus dans le perimetre utilisateur
			if (antenne.parcs) {
				let antenneSelected = true;
				for (let parc of antenne.parcs) {
					// Pour chaque parc, definir s'il est selectionne
					// a l'aide des fonctions definies precedemment
					let parcSelected = isSelectedTechno(parc.entity.energie) && isSelectedMaint(parc.entity.type) && isSelectedHyperviseur(parc.entity.hyperviseur);
					
					// Enregistrer la valeur pour le parc
					this.perimetreParcComplet[parc.entity.id] = parcSelected;

					// Repercuter l'etat du parc sur tous les sous-parcs et ouvrages
					for (let ouvrage of parc.ouvrages) {
						this.perimetreOuvrageSelected[ouvrage.id] = parcSelected;
					}
					for (let sousParc of parc.sousParcs) {
						this.perimetreSousParcComplet[sousParc.entity.id] = parcSelected;
						for (let ouvrage of sousParc.ouvrages) {
							this.perimetreOuvrageSelected[ouvrage.id] = parcSelected;
						}
					}
					
					// Si le parc n'est pas selectionne, son antenne ne sera
					// pas selectionnee non plus car incomplete
					if (!parcSelected) {
						antenneSelected = false;
					}
				}
				
				// Memoriser l'etat de selection pour l'antenne
				this.perimetreAntenneComplet[antenne.entity.id] = antenneSelected;
			}
		}
	}

	onChangeToutesAntennes(value: boolean) {
		if (this.toutesAntennes != value) {
			this.toutesAntennes = value;

			this.antennes.forEach(
				(antenne) => this.antennesSelected[antenne.id] = value
			);

			this.updateCandidats();
		}
	}

	onCheckAntenne(antenneId: number, value: boolean) {
		if (this.antennesSelected[antenneId] != value) {
			this.antennesSelected[antenneId] = value;

			this.toutesAntennes = this.antennes.every(
				(antenne) => this.antennesSelected[antenne.id]
			);

			this.updateCandidats();
		}
	}

	// Met a jour la liste des candidats possibles pour le role de CAC
	private updateCandidats() {
		let antennesSelectionees = [this.user.antenneId];
		if (this.isCce) {
			// Pour les utilisateurs CCE
			// Les candidats possibles dependent des antennes selectionnees
			antennesSelectionees = this.antennes.filter(
				(antenne) => this.antennesSelected[antenne.id]
			).map(
				(antenne) => antenne.id
			);
		}
		if (antennesSelectionees.length === 0) {
			this.candidats = [];
		} else {
			let candidats$ = this.typeResponsabilite === typeResponsabiliteCac ? 
				this.userService.listActiveCACTLRByAntennes(antennesSelectionees) : 
				this.userService.listActiveCCTLRByAntennes(antennesSelectionees)
			;
			candidats$.subscribe(
				(candidats) => this.candidats = candidats.filter( user => user.id !== this.user.id )
			);
		}
	}

	onDestinataireChange(destinataire: UserTLR | null) {
		this.destinataire = destinataire;
		this.shouldEvaluateSendAbility$.next(true);
	}

	onTechnologieChange(selection: [ParcTechnologie[], boolean]) {
		if (this.toutesTechnologies && selection[1]) {
			// Tout etait selectionne avant, tout est selectionne apres
			// => il n'y a rien a re-evaluer
			return;
		}
		
		this.selectedTechnologies = selection[0];
		this.toutesTechnologies = selection[1];

		if (!this.afficherFiltreHyperviseur) {
			this.toutesHyperviseurs = true;
			this.hyperviseurResetCommand.next();
		}

		this.updatePerimetreSelection();
		this.shouldEvaluateSendAbility$.next(true);
	}

	onMaintenanceChange(selection: [ParcMaintenance[], boolean]) {
		if (this.toutesMaintenances && selection[1]) {
			// Tout etait selectionne avant, tout est selectionne apres
			// => il n'y a rien a re-evaluer
			return;
		}
		
		this.selectedMaintenance = selection[0];
		this.toutesMaintenances = selection[1];
		this.updatePerimetreSelection();
		this.shouldEvaluateSendAbility$.next(true);
	}
	
	onHyperviseurChange(selection: [ParcHyperviseur[], boolean]) {   
		if (this.toutesHyperviseurs && selection[1]) {
			return;
		}

		this.selectedHyperviseurs = selection[0];
		this.toutesHyperviseurs = selection[1];
		this.updatePerimetreSelection();
		this.shouldEvaluateSendAbility$.next(true);
	}
	
	onChangePerimetreAntenneComplet(antenne: AntenneData, value: boolean) {
		let antenneId = antenne.entity.id;
		if (this.perimetreAntenneComplet[antenneId] === value) {
			return;
		}
		this.perimetreAntenneComplet[antenneId] = value;

		// Remplir les sous-elements d'une antenne avec la valeur saisie pour le parc lui meme
		if (antenne.parcs === null) {
			return;
		}
		for (let parc of antenne.parcs) {
			this.perimetreParcComplet[parc.entity.id] = value;
			for (let ouvrage of parc.ouvrages) {
				this.perimetreOuvrageSelected[ouvrage.id] = value;
			}
			for (let sousParc of parc.sousParcs) {
				this.perimetreSousParcComplet[sousParc.entity.id] = value;
				for (let ouvrage of sousParc.ouvrages) {
					this.perimetreOuvrageSelected[ouvrage.id] = value;
				}
			}
		}
		this.shouldEvaluateSendAbility$.next(true);
	}
	
	onChangePerimetreParcComplet(parc: ParcData, antenne: AntenneData, value: boolean) {
		
		let parcId = parc.entity.id;
		if (this.perimetreParcComplet[parcId] === value) {
			return;
		}
		this.perimetreParcComplet[parcId] = value;

		// Remplir les ouvrages et sous-parcs du parc avec la valeur saisie pour le parc lui meme
		for (let ouvrage of parc.ouvrages) {
			this.perimetreOuvrageSelected[ouvrage.id] = value;
		}
		for (let sousParc of parc.sousParcs) {
			this.perimetreSousParcComplet[sousParc.entity.id] = value;
			for (let ouvrage of sousParc.ouvrages) {
				this.perimetreOuvrageSelected[ouvrage.id] = value;
			}
		}
		this.shouldEvaluateSendAbility$.next(true);
		
		if (value) {
			// Cas de la selection d'un parc
			this.checkAntenneIfFull(antenne);
		} else {
			// Cas de la deselection d'un sous-parc
			this.perimetreAntenneComplet[antenne.entity.id] = false;
		}
	}
	
	onChangePerimetreSousParcComplet(sousParc: SousParcData, parc: ParcData, antenne: AntenneData, value: boolean) {
		let sousParcId = sousParc.entity.id;
		if (this.perimetreSousParcComplet[sousParcId] === value) {
			return;
		}
		this.perimetreSousParcComplet[sousParcId] = value;

		// Remplir les ouvrages du sous parc avec la valeur saisie pour le sous parc lui meme
		for (let ouvrage of sousParc.ouvrages) {
			this.perimetreOuvrageSelected[ouvrage.id] = value;
		}
		this.shouldEvaluateSendAbility$.next(true);
		
		if (value) {
			// Cas de la selection d'un sous-parc

			this.checkParcIfFull(parc);
			this.checkAntenneIfFull(antenne);
		} else {
			// Cas de la deselection d'un sous-parc
			
			// Decocher tous les niveaux hierarchiques precedents car ils ne peuvent plus etre "complets"
			this.perimetreParcComplet[parc.entity.id] = false;
			this.perimetreAntenneComplet[antenne.entity.id] = false;
		}
	}
	
	onChangePerimetreOuvrage(ouvrage: OuvrageTLR, sousParc: SousParcData | null, parc: ParcData, antenne: AntenneData, value: boolean) {
		let ouvrageId = ouvrage.id;
		if (this.perimetreOuvrageSelected[ouvrageId] === value) {
			return;
		}
		this.perimetreOuvrageSelected[ouvrageId] = value;
		this.shouldEvaluateSendAbility$.next(true);
		
		if (value) {
			// Cas de la selection d'un ouvrage

			if (sousParc != null) {
				this.checkSousParcIfFull(sousParc);
			}
			this.checkParcIfFull(parc);
			this.checkAntenneIfFull(antenne);
		} else {
			// Cas de la deselection d'un ouvrage
			
			// Decocher tous les niveaux hierarchiques precedents car ils ne peuvent plus etre "complets"
			if (sousParc != null) {
				this.perimetreSousParcComplet[sousParc.entity.id] = false;
			}
			this.perimetreParcComplet[parc.entity.id] = false;
			this.perimetreAntenneComplet[antenne.entity.id] = false;
		}
	}
	
	private checkSousParcIfFull(sousParc: SousParcData) {
		let allOuvragesSelected = sousParc.ouvrages.every(ouvrage => this.perimetreOuvrageSelected[ouvrage.id]);
		if (allOuvragesSelected) {
			this.perimetreSousParcComplet[sousParc.entity.id] = true;
		}
	}
	
	private checkParcIfFull(parc: ParcData) {
		let allOuvragesSelected = parc.ouvrages.every(ouvrage => this.perimetreOuvrageSelected[ouvrage.id]);
		let allSousParcsSelected = parc.sousParcs.every(sousParc => this.perimetreSousParcComplet[sousParc.entity.id]);
		if (allOuvragesSelected && allSousParcsSelected) {
			this.perimetreParcComplet[parc.entity.id] = true;
		}
	}
	
	private checkAntenneIfFull(antenne: AntenneData) {
		if (antenne.parcs == null) {
			return;
		}
		let allParcsSelected = antenne.parcs.every(parc => this.perimetreParcComplet[parc.entity.id]);
		if (allParcsSelected) {
			this.perimetreAntenneComplet[antenne.entity.id] = true;
		}
	}
	
	send() {
		let errors: string[] = [];
		
		let antennesIds = this.antennes.map($ => $.id).filter($ => this.antennesSelected[$]);
		if (antennesIds.length === 0) {
			errors.push("Veuillez sélectionner au moins une antenne.");
		}
		
		if (this.destinataire === null) {
			errors.push("Veuillez sélectionner le destinataire.");
		}
		
		let ouvragesIds: number[] | null = null;
		if (!this.transfertComplet) {
			// Si on est sur un transfert partiel, extraire les id des ouvrages selectionnes

			let ouvragesIdsTmp: number[] = [];			
			for (let antenne of this.perimetre) {
				if (antenne.parcs != null && this.antennesSelected[antenne.entity.id]) {
					for (let parc of antenne.parcs) {
						parc.ouvrages //
							.filter($ => this.perimetreOuvrageSelected[$.id]) //
							.forEach($ => ouvragesIdsTmp.push($.id)) //
						;
						for (let sousParc of parc.sousParcs) {
							sousParc.ouvrages //
								.filter($ => this.perimetreOuvrageSelected[$.id]) //
								.forEach($ => ouvragesIdsTmp.push($.id)) //
							;
						}
					}
				}
			}
			if (ouvragesIdsTmp.length === 0) {
				errors.push("Vous devez sélectionner au moins un ouvrage.");
			}
			
			ouvragesIds = ouvragesIdsTmp;
		}
		
		if (this.displayMessages('Enregistrement impossible', errors)) {
			return;
		}
		
		let date = this.conversionService.toLocalDateTime(new Date());
		
		let command = {
			antennes: antennesIds,
			destinataire: this.destinataire?.id,
			complet: this.transfertComplet,
			ouvrages: ouvragesIds,
			date: date,
			responsabilite: this.typeResponsabilite
		} as TransfertResponsabiliteOrder;
		this.waitingForServer$.next(true);
		this.transfertResponsabiliteService.askTransfert(command).pipe(
			catchError(
				(response: HttpErrorResponse) => {
					this.waitingForServer$.next(false);
					if (response.error && response.error.message) {
						alert(response.error.message);
					} else {
						alert("Erreur lors du transfert(" + response.status + ")");
					}
					throw new Error();
				}
			)
		).subscribe(
			() => {
				this.waitingForServer$.next(false);
				this.refreshCommands$.next(this.typeResponsabilite);
				this.presetValues();
				if (this.displayMessages('Compte rendu', ['Votre demande de transfert a été enregistré'])) {
					return;
				}
			}
		);
	}

	// Affiche les messages d'erreur de validation
	private displayMessages(title: string, messages: string[]): boolean {
		if (messages.length > 0) {
			this.dialog.open(MessageDialogComponent, {
				width: '400px',
				data: {
					title: title,
					messages: messages
				}
			});
			return true;
		}

		return false;
	}
}